import React, { useState, useMemo, useCallback, useEffect } from "react";
import { toast } from "sonner";
import {
  Calendar, Plus, List, CalendarDays, CalendarRange, StickyNote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AddJobDialog } from "@/components/AddJobDialog";
import { RotaListView } from "@/components/RotaListView";
import { RotaWeekView } from "@/components/RotaWeekView";
import { RotaCalendarView } from "@/components/RotaCalendarView";
import {
  ROLES,
  isStudioJob,
  getMinStaff,
  getEventType,
  EVENT_TYPE_LABELS,
  getTodayDate,
} from "@/utils/constants";

export const RotaGrid = ({ jobs, teamMembers, onUpdateJob, onDeleteJob, onAddJob, onHideJob, onConfirmStaffing, editMode, unavailableByDate, showHidden, onToggleShowHidden, onOpenJobSheet, sheetStatuses, rotaNotes, onAddNote, onDeleteNote, jumpToDate, onJumpHandled }) => {
  const [addJobOpen, setAddJobOpen] = useState(false);
  const [filterNeedsTeam, setFilterNeedsTeam] = useState(false);
  const [filterYear, setFilterYear] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [viewMode, setViewMode] = useState("list");
  const [calendarDate, setCalendarDate] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [weekStart, setWeekStart] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  
  const todayDate = getTodayDate();

  const filteredJobs = useMemo(() => jobs.filter(job => {
    if (job.date < todayDate || job.is_unavailable) return false;
    if (!showHidden && job.is_hidden) return false;
    if (filterYear !== "all" && !job.date.startsWith(filterYear)) return false;
    if (filterType !== "all") {
      const type = getEventType(job.event_name || job.title);
      if (filterType === "wedding") {
        if (!["video", "video-photo", "photo-highlight", "photo"].includes(type)) return false;
      } else if (type !== filterType) return false;
    }
    if (filterNeedsTeam) {
      const minStaff = getMinStaff(job.event_name || job.title);
      if (minStaff === 0) return false;
      const assigned = ROLES.filter(r => job.assignments?.[r]).length;
      if (assigned >= minStaff || job.staffing_confirmed) return false;
    }
    return true;
  }), [jobs, todayDate, showHidden, filterYear, filterType, filterNeedsTeam]);

  const availableYears = useMemo(() => {
    const years = new Set();
    jobs.forEach(j => {
      if (j.date >= todayDate && !j.is_unavailable) years.add(j.date.substring(0, 4));
    });
    return [...years].sort();
  }, [jobs, todayDate]);

  const conflictMap = useMemo(() => {
    const map = {};
    const visibleJobs = jobs.filter(j => j.date >= todayDate && !j.is_unavailable && !j.is_hidden && !isStudioJob(j));
    for (const job of visibleJobs) {
      if (!job.assignments) continue;
      for (const role of ROLES) {
        const memberId = job.assignments[role];
        if (!memberId) continue;
        const key = `${memberId}:${job.date}`;
        if (!map[key]) map[key] = [];
        map[key].push(job.id);
      }
    }
    return map;
  }, [jobs, todayDate]);

  const getConflict = useCallback((jobId, memberId, date) => {
    if (!memberId) return null;
    const key = `${memberId}:${date}`;
    const jobIds = conflictMap[key];
    if (!jobIds || jobIds.length <= 1) return null;
    const otherJobs = jobIds.filter(id => id !== jobId);
    if (otherJobs.length === 0) return null;
    const memberName = teamMembers.find(m => m.id === memberId)?.name || "Member";
    return { memberName, count: otherJobs.length };
  }, [conflictMap, teamMembers]);
  
  const groupedJobs = useMemo(() => filteredJobs.reduce((acc, job) => {
    const date = job.date;
    if (!acc[date]) acc[date] = [];
    acc[date].push(job);
    return acc;
  }, {}), [filteredJobs]);

  // Build notesByDate: expand each note's date range into weekdays only (Mon-Fri)
  const notesByDate = useMemo(() => {
    const map = {};
    (rotaNotes || []).forEach(note => {
      const start = new Date(note.start_date + 'T12:00:00');
      const end = new Date(note.end_date + 'T12:00:00');
      let d = new Date(start);
      while (d <= end) {
        const day = d.getDay();
        if (day !== 0 && day !== 6) { // Skip Saturday (6) and Sunday (0)
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          if (!map[key]) map[key] = [];
          map[key].push(note);
        }
        d.setDate(d.getDate() + 1);
      }
    });
    return map;
  }, [rotaNotes]);

  const sortedDates = useMemo(() => {
    const allDates = new Set(Object.keys(groupedJobs));
    // Include dates with notes or unavailability even if no jobs
    Object.keys(notesByDate).forEach(d => { if (d >= todayDate) allDates.add(d); });
    Object.keys(unavailableByDate).forEach(d => { if (d >= todayDate) allDates.add(d); });
    return [...allDates].sort((a, b) => new Date(a) - new Date(b));
  }, [groupedJobs, notesByDate, unavailableByDate, todayDate]);
  
  const formatDate = useCallback((dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }, []);

  const getDisplayTitle = useCallback((job) => {
    if (job.event_name) return job.event_name;
    let title = job.title;
    title = title.replace(/^Confirmed\s*-\s*/i, '');
    title = title.replace(/^Contract Issued\s*-\s*/i, '');
    return title;
  }, []);

  const handleAssignmentChange = useCallback(async (jobId, role, memberId) => {
    if (!editMode) return;
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;
    
    if (memberId) {
      const memberName = teamMembers.find(m => m.id === memberId)?.name || "Member";
      const unavailable = unavailableByDate[job.date] || [];
      if (unavailable.includes(memberName)) {
        const override = window.confirm(`${memberName} is marked as unavailable on ${job.date}.\n\nAre you sure you want to assign them anyway?`);
        if (!override) return;
        toast.warning(`${memberName} assigned despite being unavailable`, { duration: 3000 });
      }

      if (!isStudioJob(job)) {
        const key = `${memberId}:${job.date}`;
        const existing = conflictMap[key];
        if (existing && existing.length > 0 && !existing.includes(jobId)) {
          toast.warning(`${memberName} is already assigned to another job on this date`, { duration: 3000 });
        }
      }
    }
    
    const newAssignments = { ...job.assignments, [role]: memberId || null };
    await onUpdateJob(jobId, { assignments: newAssignments });
  }, [editMode, jobs, conflictMap, teamMembers, onUpdateJob, unavailableByDate]);

  const handleVideoBoothChange = useCallback(async (jobId, checked) => {
    if (!editMode) return;
    await onUpdateJob(jobId, { has_video_booth: checked });
  }, [editMode, onUpdateJob]);

  const recentChangeDates = useMemo(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const dates = new Set();
    jobs.forEach(job => {
      if (!job.assignments_updated_at) return;
      const changedAt = new Date(job.assignments_updated_at);
      if (changedAt >= sevenDaysAgo) {
        dates.add(job.date);
      }
    });
    return [...dates].sort();
  }, [jobs]);

  const scrollToDate = useCallback((date) => {
    setViewMode('list');
    setTimeout(() => {
      const el = document.getElementById(`date-${date}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
  }, []);

  useEffect(() => {
    if (jumpToDate) {
      scrollToDate(jumpToDate);
      onJumpHandled?.();
    }
  }, [jumpToDate, scrollToDate, onJumpHandled]);

  const calendarJobsByDate = useMemo(() => {
    const map = {};
    jobs.forEach(job => {
      if (job.is_unavailable || (!showHidden && job.is_hidden)) return;
      if (filterType !== "all") {
        const type = getEventType(job.event_name || job.title);
        if (filterType === "wedding") {
          if (!["video", "video-photo", "photo-highlight", "photo"].includes(type)) return;
        } else if (type !== filterType) return;
      }
      if (filterNeedsTeam) {
        const minStaff = getMinStaff(job.event_name || job.title);
        if (minStaff === 0) return;
        const assigned = ROLES.filter(r => job.assignments?.[r]).length;
        if (assigned >= minStaff || job.staffing_confirmed) return;
      }
      if (!map[job.date]) map[job.date] = [];
      map[job.date].push(job);
    });
    return map;
  }, [jobs, showHidden, filterType, filterNeedsTeam]);

  // Calendar navigation
  const prevMonth = useCallback(() => setCalendarDate(prev => prev.month === 0 ? { year: prev.year - 1, month: 11 } : { ...prev, month: prev.month - 1 }), []);
  const nextMonth = useCallback(() => setCalendarDate(prev => prev.month === 11 ? { year: prev.year + 1, month: 0 } : { ...prev, month: prev.month + 1 }), []);
  const goToToday = useCallback(() => { const n = new Date(); setCalendarDate({ year: n.getFullYear(), month: n.getMonth() }); }, []);

  // Week navigation
  const prevWeek = useCallback(() => setWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() - 7); return d; }), []);
  const nextWeek = useCallback(() => setWeekStart(prev => { const d = new Date(prev); d.setDate(d.getDate() + 7); return d; }), []);
  const goToThisWeek = useCallback(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    setWeekStart(monday);
  }, []);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Recent Changes Banner */}
      {recentChangeDates.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5" data-testid="recent-changes-banner">
          <p className="text-xs font-medium text-blue-700 mb-1.5">Recent assignment changes</p>
          <div className="flex flex-wrap gap-1.5">
            {recentChangeDates.map(date => (
              <button
                key={date}
                onClick={() => scrollToDate(date)}
                className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors cursor-pointer"
                data-testid={`recent-change-${date}`}
              >
                {formatDate(date)}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 sm:gap-4 text-sm text-[#8d8b8b] flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <span>{filteredJobs.length} jobs</span>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Select value={filterNeedsTeam ? "needs-team" : "all"} onValueChange={(v) => setFilterNeedsTeam(v === "needs-team")}>
              <SelectTrigger className="h-7 w-[115px] text-xs" data-testid="filter-needs-team">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staffing</SelectItem>
                <SelectItem value="needs-team">Needs Team</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-7 w-[140px] sm:w-[165px] text-xs" data-testid="filter-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(EVENT_TYPE_LABELS).map(([val, label]) => (
                  <SelectItem key={val} value={val}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="h-7 w-[80px] text-xs" data-testid="filter-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {availableYears.map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {editMode && (
            <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="show-hidden-toggle">
              <Checkbox checked={showHidden} onCheckedChange={onToggleShowHidden} />
              <span className="text-xs">Show hidden</span>
            </label>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center border border-[#e5e7eb] rounded-md overflow-hidden" data-testid="view-toggle">
            <button
              onClick={() => setViewMode('list')}
              className={`px-2 py-1 text-xs flex items-center gap-1 transition-colors ${viewMode === 'list' ? 'bg-[#605e5e] text-white' : 'bg-white text-[#605e5e] hover:bg-gray-50'}`}
              data-testid="view-list-btn"
            >
              <List className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">List</span>
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-2 py-1 text-xs flex items-center gap-1 transition-colors ${viewMode === 'week' ? 'bg-[#605e5e] text-white' : 'bg-white text-[#605e5e] hover:bg-gray-50'}`}
              data-testid="view-week-btn"
            >
              <CalendarRange className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Week</span>
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-2 py-1 text-xs flex items-center gap-1 transition-colors ${viewMode === 'calendar' ? 'bg-[#605e5e] text-white' : 'bg-white text-[#605e5e] hover:bg-gray-50'}`}
              data-testid="view-calendar-btn"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Calendar</span>
            </button>
          </div>
          {editMode && (
            <>
              <Button
                onClick={() => setAddJobOpen(true)}
                size="sm"
                className="bg-[#605e5e] hover:bg-[#4b4a4a] h-8"
                data-testid="open-add-job-btn"
              >
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Add Job</span>
              </Button>
              <Button
                onClick={onAddNote}
                size="sm"
                variant="outline"
                className="border-[#e5e7eb] text-[#605e5e] hover:bg-gray-50 h-8"
                data-testid="open-add-note-btn"
              >
                <StickyNote className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Add Note</span>
              </Button>
            </>
          )}
        </div>
      </div>
      
      {viewMode === 'list' && (
        <RotaListView
          sortedDates={sortedDates}
          groupedJobs={groupedJobs}
          filteredJobs={filteredJobs}
          teamMembers={teamMembers}
          editMode={editMode}
          unavailableByDate={unavailableByDate}
          recentChangeDates={recentChangeDates}
          onAssignmentChange={handleAssignmentChange}
          onVideoBoothChange={handleVideoBoothChange}
          onHideJob={onHideJob}
          onDeleteJob={onDeleteJob}
          onConfirmStaffing={onConfirmStaffing}
          onOpenJobSheet={onOpenJobSheet}
          sheetStatuses={sheetStatuses}
          getConflict={getConflict}
          formatDate={formatDate}
          getDisplayTitle={getDisplayTitle}
          notesByDate={notesByDate}
          onDeleteNote={onDeleteNote}
        />
      )}

      {viewMode === 'week' && (
        <RotaWeekView
          weekStart={weekStart}
          onPrevWeek={prevWeek}
          onNextWeek={nextWeek}
          onGoToThisWeek={goToThisWeek}
          calendarJobsByDate={calendarJobsByDate}
          teamMembers={teamMembers}
          unavailableByDate={unavailableByDate}
          getDisplayTitle={getDisplayTitle}
          onOpenJobSheet={onOpenJobSheet}
          sheetStatuses={sheetStatuses}
          editMode={editMode}
          onHideJob={onHideJob}
          onDeleteJob={onDeleteJob}
        />
      )}

      {viewMode === 'calendar' && (
        <RotaCalendarView
          calendarDate={calendarDate}
          onSetMonth={(year, month) => setCalendarDate({ year, month })}
          onPrevMonth={prevMonth}
          onNextMonth={nextMonth}
          onGoToToday={goToToday}
          calendarJobsByDate={calendarJobsByDate}
          teamMembers={teamMembers}
          unavailableByDate={unavailableByDate}
          recentChangeDates={recentChangeDates}
          getDisplayTitle={getDisplayTitle}
          onOpenJobSheet={onOpenJobSheet}
          sheetStatuses={sheetStatuses}
          editMode={editMode}
          onHideJob={onHideJob}
          onDeleteJob={onDeleteJob}
        />
      )}
      
      {editMode && (
        <AddJobDialog 
          open={addJobOpen} 
          onClose={() => setAddJobOpen(false)} 
          onAdd={onAddJob}
        />
      )}
    </div>
  );
};
